/**
 * Browser side of the PDF builders: fetch the template that ships with the
 * app (public/pdf), build, and hand the file to the share sheet / download.
 * pdf-lib (~500 KB) loads only when someone makes a PDF.
 */
import { shareFile } from '../share';
import type { MmrInput, Op201Input } from './fill';

async function template(name: 'mmr' | 'op201'): Promise<ArrayBuffer> {
  const res = await fetch(`/pdf/${name}-template.pdf`);
  if (!res.ok) throw new Error(`Couldn't load the ${name.toUpperCase()} form`);
  return res.arrayBuffer();
}

const blob = (bytes: Uint8Array) => new Blob([bytes as BlobPart], { type: 'application/pdf' });

export async function downloadMmr(input: MmrInput, filename: string): Promise<'shared' | 'cancelled' | 'downloaded'> {
  const [{ buildMmr }, tpl] = await Promise.all([import('./fill'), template('mmr')]);
  return shareFile(blob(await buildMmr(tpl, input)), filename, filename.replace(/\.pdf$/, ''));
}

export async function downloadOp201(input: Op201Input, filename: string): Promise<'shared' | 'cancelled' | 'downloaded'> {
  const [{ buildOp201 }, tpl] = await Promise.all([import('./fill'), template('op201')]);
  return shareFile(blob(await buildOp201(tpl, input)), filename, 'FedEx OP-201');
}

/** "Hidden Arrow Inc." → "Hidden-Arrow-Inc" for file names. */
export const fileSafe = (s: string): string => s.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'RouteBack';

export async function downloadAccident(input: import('./fill').AccidentPdfInput, filename: string): Promise<'shared' | 'cancelled' | 'downloaded'> {
  const { buildAccidentSummary } = await import('./fill');
  return shareFile(blob(await buildAccidentSummary(input)), filename, 'Accident report');
}
